<script setup lang="ts">
import { computed, isVNode, ref, watch, type VNode } from 'vue';

import { messageAlertState, type MessageAlertOptions } from '@/shared/ui/message-alert';
import { WarningFilled } from '@element-plus/icons-vue';

// Renders a caller-supplied VNode body. A stable functional component, not an
// inline `:is` object — the latter is a fresh component definition per render,
// which remounts the body on every state change.
const VNodeRenderer = (props: { vnode: VNode }) => props.vnode;

const value = ref('');
const submitting = ref(false);
// Holds the last-shown options so the button/icon theme stays put through the
// dialog's leave transition. Reading straight from messageAlertState (nulled on
// close) would drop `theme` mid-animation, flashing a danger button to primary.
const opts = ref<MessageAlertOptions>();
const open = computed({
  get: () => !!messageAlertState.value,
  set: (v) => {
    if (!v && !submitting.value) onCancel();
  },
});

// `immediate` because `open` reads the state directly: a dialog opened before
// this host mounts would otherwise leave `opts` undefined, rendering an empty
// dialog that grows a Cancel button (`type !== 'alert'` is true for undefined).
watch(
  messageAlertState,
  (state) => {
    if (state) {
      value.value = '';
      submitting.value = false;
      opts.value = state.options;
    }
  },
  { immediate: true },
);
const showCancel = computed(() => opts.value?.type !== 'alert');
const showInput = computed(() => opts.value?.type === 'prompt');
const cancelText = computed(() => opts.value?.cancelText ?? 'Cancel');
const confirmText = computed(() => opts.value?.confirmText ?? 'Confirm');
const iconColor = computed(() =>
  opts.value?.theme === 'danger' ? 'text-danger' : 'text-brand-azure',
);
const canConfirm = computed(() => !opts.value?.checkInput || opts.value.checkInput === value.value);

async function onConfirm(): Promise<void> {
  const state = messageAlertState.value;
  if (!state || !canConfirm.value || submitting.value) return;
  const run = state.options.onConfirm;
  if (!run) {
    state.resolve(value.value);
    messageAlertState.value = null;
    return;
  }
  submitting.value = true;
  try {
    await run();
    state.resolve(value.value);
    messageAlertState.value = null;
  } catch {
    // Async confirm rejected: keep the dialog open so the user can retry.
    // Error surfacing is the caller's onConfirm (the axios interceptor toasts).
  } finally {
    submitting.value = false;
  }
}

function onCancel(): void {
  const state = messageAlertState.value;
  if (!state || submitting.value) return;
  state.reject(new Error('cancelled'));
  messageAlertState.value = null;
}
</script>

<template>
  <ElDialog
    v-model="open"
    :show-close="false"
    :close-on-click-modal="false"
    :close-on-press-escape="!submitting"
    width="600px"
    class="sr-message-alert"
    data-testid="alert-dialog"
  >
    <template #header>
      <div class="text-fg flex items-center gap-2 text-base font-semibold">
        <ElIcon :size="18" class="shrink-0" :class="iconColor">
          <WarningFilled />
        </ElIcon>
        <!-- Titles interpolate a user-supplied name ("remove the role X?"), so
             the run may have no break opportunity at all — break anywhere rather
             than let it escape the dialog box. -->
        <span class="min-w-0 break-all">{{ opts?.title }}</span>
      </div>
    </template>
    <div>
      <!-- Bodies interpolate names and emails too ("unassign X from Y"), so they
           need the same escape hatch as the title — `wrap-break-word` rather than
           `break-all` because this is prose: only an unbreakable run splits. -->
      <div v-if="isVNode(opts?.content)" class="text-fg wrap-break-word">
        <VNodeRenderer :vnode="opts!.content as VNode" />
      </div>
      <div v-else-if="opts?.content" class="text-fg wrap-break-word whitespace-pre-line">
        {{ opts.content }}
      </div>
      <ElInput v-if="showInput" v-model="value" :placeholder="opts?.placeholder" class="mt-4" />
    </div>
    <template #footer>
      <div class="flex justify-end gap-2">
        <ElButton
          v-if="showCancel"
          text
          :disabled="submitting"
          data-testid="alert-cancel-btn"
          @click="onCancel"
        >
          {{ cancelText }}
        </ElButton>
        <ElButton
          :type="opts?.theme === 'danger' ? 'danger' : 'primary'"
          :loading="submitting"
          :disabled="!canConfirm || submitting"
          data-testid="alert-confirm-btn"
          @click="onConfirm"
        >
          {{ confirmText }}
        </ElButton>
      </div>
    </template>
  </ElDialog>
</template>

<style>
.sr-message-alert.el-dialog {
  padding: 0;
}
.sr-message-alert .el-dialog__header {
  padding: 30px 24px 16px;
  margin-right: 0;
}
.sr-message-alert .el-dialog__body {
  padding: 0 24px;
}
.sr-message-alert .el-dialog__footer {
  padding: 24px 24px 16px;
}
</style>
